import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetTireAspectRatioQuery,
  usePatchTireAspectRatioMutation,
  usePostTireAspectRatioMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {TireDictionaryForm} from '../../components/TireDictionaryForm/TireDictionaryForm';
import {TireDictionaryFormType} from '../../types/TireDictionaryFormType';

export function TireInventoryAspectRatioDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetTireAspectRatioQuery(
    {tireAspectRatioId: id ?? ''},
    {skip: isNil(id)}
  );

  const [postTireAspectRatioApi] = usePostTireAspectRatioMutation();
  const [patchTireAspectRatioApi] = usePatchTireAspectRatioMutation();

  const handleSubmit: FormSubmitHandler<TireDictionaryFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      name: formData.name,
    };

    if (isNil(id)) {
      return await postTireAspectRatioApi({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.tireAspectRatio);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchTireAspectRatioApi({
      tireAspectRatioId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireAspectRatio);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{
        title: data?.name ?? i18n.t('entity.tiresInventory.labels.newTireAspectRatio'),
        breadcrumbs: [
          {
            label: i18n.t('module.tiresInventory.title'),
            isCurrentPage: true,
          },
          {
            label: i18n.t('entity.tiresInventory.labels.tireAspectRatio'),
            href: settingsRoutes.tireAspectRatio,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      isCreating={isNil(id)}
      data-testid={testIds.settings.tireAspectRatioDetail('template')}
    >
      <TireDictionaryForm
        onSubmit={handleSubmit}
        defaultValues={{name: data?.name}}
        onDiscard={(navigateWithoutBlocker) =>
          navigateWithoutBlocker(settingsRoutes.tireAspectRatio)
        }
        data-testid={testIds.settings.tireAspectRatioDetail('form')}
      />
    </SettingsTemplate>
  );
}
